<template>

  <div>
    <teleport to="#topic-search">
      <n-search-container
        v-if="!isEdit"
        :fields="searchFields"
        @search="search"
        @reset="reset"
      >
        <n-search-input
          ref="questSearch"
          v-model="params"
          :fields="searchFields"
        >
          <template #startDate>
            <div v-if="params.period != 'other'" />
          </template>
          <template #endDate>
            <div v-if="params.period != 'other'" />
          </template>
        </n-search-input>
      </n-search-container>
    </teleport>
    <load-profile />
    <b-card
      v-if="!isEdit"
      no-body
      class="mb-0"
    >
      <div class="p-2">
        <div class="d-flex lang-container">
          <div>
            <label>{{ $t("general.entries") }}</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block ml-50 mr-1 w-20"
            >
              <template #no-options>
                {{ $t("selection.noOption") }}
              </template>
            </v-select>
          </div>

          <div
            v-if="$can('create', permissionType)"
            class="btn-filter-bot"
          >
            <n-button-loading
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              variant="primary"
              class="btn-submit-bot-filter"
              :loading="loadingDownload"
              @submit="download"
            >
              {{ $t("button.download") }}
            </n-button-loading>
          </div>
        </div>
      </div>
      <div>
        <n-table
          ref="table"
          :fields="fields"
          :items="items"
          :busy="loading"
          :current-page="params.page"
          :per-page="$store.state.pagination.perPage"
          :total="total"
          :deletable="true"
          :type="permissionType"
          :route="route"
          :updatable="false"
          @destroy="destroy"
        >
          <template #cell(text)="data">
            <span v-if="data.item.text">
              <p
                v-for="(text, index) in data.item.text"
                :key="index"
              >
                - {{ text }}
              </p>
            </span>
          </template>
          <template #cell(chatChannelName)="data">
            <span v-if="data.item.channel">
              {{ data.item.channel.name }}
            </span>
          </template>
          <template #cell(topics)="data">
            <span
              v-for="(topic, index) in data.item.topics"
              :key="index"
              class="d-block mb-1"
            >
              {{ getTopicByCurrentLang(topic) }}
            </span>
          </template>
          <template #cell(conversationDate)="data">
            <span
              v-if="data.item.logDate"
              class="d-block mb-1"
            >
              {{ data.item.logDate | formatDate("LL") }}
            </span>
          </template>
          <template #front-button="data">
            <b-button
              v-b-tooltip.hover
              :title="$t('breadcrumb.chat')"
              variant="info"
              pill
              size="sm"
              @click="redirectToChatRoom(data.item)"
            >
              <feather-icon icon="MessageCircleIcon" />
            </b-button>
          </template>
        </n-table>
        <n-pagination
          class="mt-2"
          :total="total"
          :per-page="$store.state.pagination.perPage"
          :page="query.page"
          @change="list"
        />
      </div>
    </b-card>
    <b-card
      v-else
      no-body
      class="mb-0"
    >
      <h4 class="mb-3">
        {{ $t("breadcrumb.edit_question") }}
      </h4>
      <validation-observer
        ref="editForm"
        #default="{ invalid }"
      >
        <!-- form -->
        <n-form-confirmation
          key="editForm"
          :form="$refs.editForm"
          :disabled="invalid"
          @submit="validateForm"
        >
          <n-input
            v-model="data"
            :fields="updateForm"
            :init-value="initData"
          />
          <b-row>
            <b-col
              cols="12"
              class="text-right"
            >
              <n-button-loading
                v-if="
                  $can('update', permissionType) &&
                    data.type != 'default-merchant'
                "
                v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                type="submit"
                class="mt-1 mr-1"
                variant="primary"
                :loading="loading"
                :disabled="invalid"
              >
                {{ $t("button.saveChanges") }}
              </n-button-loading>
              <n-button-loading
                v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                type="button"
                variant="outline-secondary"
                class="mt-1"
                @submit="back"
              >
                {{ $t("button.back") }}
              </n-button-loading>
            </b-col>
          </b-row>
        </n-form-confirmation>
      </validation-observer>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BBadge,
  BLink,
  BSpinner,
  BButton,
  BForm,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import Repository from '@/repositories/RepositoryFactory'
import NPagination from '@/components/NPagination'
import NTable from '@/components/NTable'
import { avatarText } from '@core/utils/filter'
import NInput from '@/components/NInput'
import NButtonLoading from '@/components/NButtonLoading'
import Ripple from 'vue-ripple-directive'
import NSearchInput from '@/components/NSearchInput'
import teleport from '@/helpers/teleport'
import NSearchContainer from '@/components/NSearchContainer.vue'
import { calculateDatesByPeriod } from '@/libs/helper'
import NFormConfirmation from '@/components/NFormConfirmation'
import * as XLSX from 'xlsx'
import tableFields from './tableField'
import formInput from './updateQuestionFrom'
import SearchInputs from './searchInput'

const TopicSummaryRepository = Repository.get('question')

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BLink,
    BBadge,
    BSpinner,
    NSearchContainer,
    BButton,
    BForm,
    NPagination,
    NTable,
    vSelect,
    NInput,
    NButtonLoading,
    teleport,
    NSearchInput,
    NFormConfirmation,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      items: [],
      loading: false,
      resendLoading: false,
      total: 0,
      perPageOptions: this.$store.state.pagination.perPageOptions,
      params: {
        period: this.$route.query.period || 'thisWeek',
        startDate: this.$route.query.startDate || null,
        endDate: this.$route.query.endDate || null,
      },
      query: {
        page: Number(this.$route.query.page) || 1,
        limit: Number(this.$route.query.limit) || 25,
      },
      avatarText,
      data: {
        question: '',
        answer: '',
      },
      options: [],
      listLanguage: [],
      initData: {},
      loadingDownload: false,
      selectedId: null,
      isEdit: false,
    }
  },
  computed: {
    perPage: {
      get() {
        return this.$store.state.pagination.perPage
      },
      set(value) {
        this.$store.commit('pagination/SET_PER_PAGE', value)
      },
    },
  },
  watch: {
    perPage() {
      this.list(1)
    },
    'params.period': function (value) {
      this.calculateDate()
    },
  },

  mounted() {
    this.getData()
  },
  methods: {
    updateQuerySting() {
      this.$router.replace({ query: this.query })
    },
    list(page = 1) {
      this.query.page = page
      this.query.limit = this.perPage
      this.updateQuerySting()
      this.getData()
    },
    onChange(value) {
      if (value?.languageId) {
        this.query.lang = value?.languageId
        this.getData()
      }
    },
    getData() {
      this.loading = true
      if (!this.params.startDate || !this.params.endDate) {
        this.calculateDate()
      } else {
        this.formatIsoTimeString()
      }
      TopicSummaryRepository.topicSummary({
        ...this.query,
      })
        .then(response => {
          const { list } = response.data.data

          this.items = [...list]
          this.total = response.data.data.total
        })
        .catch(() => {})
        .then(() => {
          this.loading = false
        })
    },
    destroy(id) {
      TopicSummaryRepository.destroyTopicSummary(id)
        .catch(err => {
          console.log('err', err)
        })
        .finally(() => {
          this.getData()
        })
    },
    calculateDate() {
      let dates
      if (this.params.period == 'other') {
        dates = calculateDatesByPeriod('thisWeek')
      } else {
        dates = calculateDatesByPeriod(this.params.period)
      }
      if (dates.length) {
        // Set start date to 12:00:00 AM
        const startDate = new Date(dates[0])
        startDate.setHours(0, 0, 0, 0)

        // Set end date to 11:59:59 PM
        const endDate = new Date(dates[1])
        endDate.setHours(23, 59, 59, 999)

        this.params.startDate = startDate.toISOString()
        this.params.endDate = endDate.toISOString()
        this.query.startDate = startDate.toISOString()
        this.query.endDate = endDate.toISOString()
      }
    },
    formatIsoTimeString() {
      const startDate = new Date(this.params.startDate)
      startDate.setHours(0, 0, 0, 0)
      // Set end date to 11:59:59 PM
      const endDate = new Date(this.params.endDate)
      endDate.setHours(23, 59, 59, 999)
      this.query.startDate = startDate.toISOString()
      this.query.endDate = endDate.toISOString()
    },
    search() {
      this.list()
    },
    reset() {
      this.$refs.questSearch.reset()
      this.params.period = 'thisWeek'
      this.calculateDate()
      this.updateQuerySting()
      this.getData()
    },
    validateForm() {
      this.$refs.editForm.validate().then(async success => {
        if (success) {
          await this.submit()
        }
      })
    },
    async submit() {
      this.loading = true
      TopicSummaryRepository.update(this.selectedId, this.data)
        .catch(error => {
          if (error.response?.status == 422) {
            this.$refs.editForm.setErrors(error.response?.data?.message)
          }
        })
        .then(() => {
          this.getData()
          this.isEdit = false
          this.loading = false
        })
    },
    clickEdit(questionData) {
      this.selectedId = questionData.id
      this.data.question = questionData.question
      this.data.answer = questionData.answer
      this.isEdit = true
    },
    back() {
      this.isEdit = false
    },
    download() {
      this.loadingDownload = true
      if (!this.params.startDate || !this.params.endDate) {
        this.calculateDate()
      } else {
        this.formatIsoTimeString()
      }

      TopicSummaryRepository.downloadTopicSummary({
        startDate: this.params.startDate,
        endDate: this.params.endDate,
        lang: this.$i18n.locale.split('-')[0] === 'zh' ? 'zh' : 'en',
      })
        .then(async response => {
          let arrayBuffer

          // Determine the array buffer from the response
          if (response.data instanceof ArrayBuffer) {
            arrayBuffer = response.data
          } else if (response.arrayBuffer) {
            arrayBuffer = await response.arrayBuffer()
          } else if (response instanceof ArrayBuffer) {
            arrayBuffer = response
          } else {
            arrayBuffer = new TextEncoder().encode(response.data || response).buffer
          }

          // Read the workbook from the buffer
          const workbook = XLSX.read(arrayBuffer, { type: 'array' })

          // Write the workbook to an .xlsx file
          const excelData = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })

          // Create a Blob and trigger download
          const blob = new Blob([excelData], { type: 'application/octet-stream' })
          const url = URL.createObjectURL(blob)
          const link = document.createElement('a')

          // Generate file name based on start and end date
          const fileName = `topic_report_${this.params.startDate
            .split('T')[0]
            .replace(/[^a-z0-9]/gi, '_')}-${this.params.endDate
            .split('T')[0]
            .replace(/[^a-z0-9]/gi, '_')}.xlsx`

          link.href = url
          link.setAttribute('download', fileName)
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
        })
        .catch(error => {
          console.error('Download failed:', error)
        })
        .finally(() => {
          this.loadingDownload = false
        })
    },
    getTopicByCurrentLang(topic) {
      const currentLang = this.$i18n.locale.split('-')[0]
      return topic[currentLang] ? topic[currentLang] : topic.en
    },
    redirectToChatRoom(data) {
      const url = this.$router.resolve({
        name: 'chat',
        query: {
          from: 'topic-summary',
          channelId: data.channel.id,
          msgStartDate: data.logDate,
        },
      })
      window.open(url.href, '_blank')
    },
  },
  setup() {
    const fields = tableFields
    const searchFields = SearchInputs
    const updateForm = formInput
    const permissionType = 'summary-conversation'
    const route = 'topic'

    return {
      fields,
      searchFields,
      permissionType,
      route,
      updateForm,
    }
  },
}
</script>

<style lang="scss">
.lang-input span label {
  display: none !important;
}

.lang-container {
  width: 100%;
  gap: 10px;
}

.btn-filter-bot {
  display: flex;
  flex: 1;
  justify-content: end;
}

.btn-submit-bot-filter {
  height: fit-content;
}

.w-20 {
  width: 100px;
}

.lang-container .v-select.vs--single.vs--searchable {
  width: 150px;
}

.displays {
  display: flex;
  justify-items: center;
}
.btnRetrain {
  border: none;
  background: rgb(6, 172, 67);
  color: rgb(255, 255, 255);
  margin-left: 7px;
  border-radius: 5px;
  cursor: pointer;
}
@import "@core/scss/vue/libs/vue-select.scss";
</style>
